import React, { useState, useEffect } from "react";
import { VITE_SUPABASE_URL } from "@/utils/const";
import { LocationStep } from "./onboarding/LocationStep";
import { BirthsAttendedStep } from "./onboarding/BirthsAttendedStep";
import { ProfessionalInfoStep } from "./onboarding/ProfessionalInfoStep";
import { ExperienceStep } from "./onboarding/ExperienceStep";
import { ProfilePreferencesStep } from "./onboarding/ProfilePreferencesStep";
import { ExcludedProvidersStep } from "./onboarding/ExcludedProvidersStep";
import { PackagesStep } from "./onboarding/PackagesStep";
import { PhotoUploadStep } from "./onboarding/PhotoUploadStep";
import { CertificationsStep } from "./onboarding/CertificationsStep";
import { VideoStep } from "./onboarding/VideoStep";
import { CalendarStep } from "./onboarding/CalendarStep";
import { MonthsAvailableStep } from "./onboarding/MonthsAvailableStep";
import { AvailabilitySettingsStep } from "./onboarding/AvailabilitySettingsStep";
import { StripeStep } from "./onboarding/StripeStep";
import { CompletionStep } from "./onboarding/CompletionStep";
import { supabase } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";

interface Provider {
	provider_name: string;
	provider_address: string;
	provider_place_id: string | null;
	provider_types: string;
	provider_formatted_address: string;
	provider_city: string;
	provider_state: string;
	provider_zip: number | string;
	provider_latitude: number | null;
	provider_longitude: number | null;
}

interface Package {
	name: string;
	description: string;
	price: number | null;
	discount_percentage: number | null;
}

interface StepProps {
	onNext: (stepData?: unknown) => void;
	onBack: () => void;
	initialData?: {
		[key: string]: unknown;
	};
}

export const DoulaOnboarding = () => {
	const [currentStep, setCurrentStep] = useState(1);
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();
	const { user, profile, loading: authLoading } = useAuth();

	useEffect(() => {
		// Wait for auth to be determined
		if (authLoading) {
			return;
		}

		// Check if user is authenticated
		if (!user) {
			navigate("/doula-signup");
			return;
		}

		// Check if user is a doula using auth metadata
		const userRole = user.app_metadata?.role || user.user_metadata?.user_type;

		if (userRole !== "doula") {
			navigate("/doula-signup");
			return;
		}

		// Load onboarding data using edge function
		const loadOnboardingData = async () => {
			try {
				const response = await fetch(
					`${VITE_SUPABASE_URL}/functions/v1/get-doula-onboarding-data`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
						},
					},
				);

				const result = await response.json();

				if (!result.success) {
					throw new Error(result.error || "Failed to load onboarding data");
				}

				const {
					user: userData,
					doula_listing,
					experiences,
					certifications,
					packages,
					excluded_providers,
				} = result.data;

				if (userData?.onboarding_completed) {
					navigate("/doula-dashboard");
					return;
				}

				// Set current step based on database
				const stepMap: { [key: string]: number } = {
					location: 1,
					births_attended: 2,
					professional_info: 3,
					experience: 4,
					profile_preferences: 5,
					excluded_providers: 6,
					packages: 7,
					photo: 8,
					certifications: 9,
					video: 10,
					calendar: 11,
					months_available: 12,
					availability_settings: 13,
					stripe: 14,
					completion: 15,
				};

				if (userData?.onboarding_step && stepMap[userData.onboarding_step]) {
					setCurrentStep(stepMap[userData.onboarding_step]);
				}

				// Populate formData with existing data from database
				if (doula_listing) {
					const existingData = {
						// Location data
						address: doula_listing.location_address || "",
						city: doula_listing.location_city || "",
						state: doula_listing.location_state || "",
						zip: doula_listing.location_zip || "",
						latitude: doula_listing.latitude || null,
						longitude: doula_listing.longitude || null,
						timezone: doula_listing.timezone || "",

						// Births attended
						birthsAttended: doula_listing.births_attended_range || "",

						// Professional info
						bio: doula_listing.bio || "",
						servicesOffered: doula_listing.services_offered || [],

						// Experience
						currentTitle: doula_listing.title || "",
						yearsExperience: doula_listing.years_experience?.toString() || "",
						experiences: experiences || [],

						// Excluded Providers - map database fields to component expected fields
						excludedProviders: (excluded_providers || []).map((provider: Provider) => ({
							name: provider.provider_name,
							address: provider.provider_address,
							place_id: provider.provider_place_id,
							types: provider.provider_types,
							formatted_address: provider.provider_formatted_address,
							city: provider.provider_city,
							state: provider.provider_state,
							zip: provider.provider_zip,
							latitude: provider.provider_latitude,
							longitude: provider.provider_longitude,
						})),

						// Packages - map database fields to component expected fields
						packages: (packages || []).map((pkg: Package) => ({
							name: pkg.name,
							description: pkg.description,
							price: pkg.price?.toString() || "0.00",
							discountPercentage: pkg.discount_percentage?.toString() || "0",
						})),

						// Photo
						photoUrl: doula_listing.profile_photo_url || "",

						// Certifications - map database fields to component expected fields
						certifications: (certifications || []).map((cert) => ({
							name: cert.name,
							description: cert.description,
							issueDate: cert.issue_date,
							certificateUrl: cert.certificate_url,
						})),

						// Video
						videoUrl: doula_listing.intro_video_url || "",

						// Calendar
						calendarId: doula_listing.google_calendar_id || "",
						calendarName: doula_listing.google_calendar_id
							? "Connected Calendar"
							: "",

						// Months Available
						monthsAvailable: doula_listing.months_available || {
							january: true,
							february: true,
							march: true,
							april: true,
							may: true,
							june: true,
							july: true,
							august: true,
							september: true,
							october: true,
							november: true,
							december: true,
						},

						// Availability
						availabilityGuardrails: doula_listing.availability_guardrails || {
							monday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
							tuesday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
							wednesday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
							thursday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
							friday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
							saturday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
							sunday: {
								available: false,
								startTime: "09:00",
								endTime: "17:00",
							},
						},
						bookingWindowMonths: doula_listing.booking_window_months || 6,

						// Profile Preferences
						additionalSpecialties: doula_listing.additional_specialties || [],
						gender: doula_listing.gender || "",
						sexuality: doula_listing.sexuality || "",
						race: doula_listing.race || "",
						religion: doula_listing.religion || "",
						style: doula_listing.style || [],
						doulaServiceType: doula_listing.doula_service_type || [],
						languagesSpoken: doula_listing.languages_spoken || [],

						// Stripe
						stripeAccountId: doula_listing.stripe_connect_account_id || "",
					};

					setFormData(existingData);
				}
			} catch (error) {
				console.error("Error loading onboarding data:", error);
			} finally {
				setLoading(false);
			}
		};

		loadOnboardingData();
	}, [user, authLoading, navigate]);

	const handleNext = async (stepData: any) => {
		if (!user) {
			console.error("User not available");
			return;
		}

		const updatedData = { ...formData, ...stepData };
		setFormData(updatedData);

		// Update onboarding step via edge function
		const stepNames = [
			"location",
			"births_attended",
			"professional_info",
			"experience",
			"profile_preferences",
			"excluded_providers",
			"packages",
			"photo",
			"certifications",
			"video",
			"calendar",
			"months_available",
			"availability_settings",
			"stripe",
			"completion",
		];
		const nextStepName = stepNames[currentStep] || "completion";

		try {
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-user-onboarding`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						userId: user.id,
						onboardingStep: nextStepName,
						onboardingCompleted: currentStep === 15,
					}),
				},
			);

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.error || "Failed to update onboarding step");
			}
		} catch (error) {
			console.error("Error updating onboarding step:", error);
		}

		if (currentStep === 15) {
			// Final step - complete onboarding
			await saveOnboardingData(updatedData);
		} else {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleBack = () => {
		setCurrentStep(Math.max(1, currentStep - 1));
	};

	const saveOnboardingData = async (data: any) => {
		try {
			// Mark onboarding as completed via edge function
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "completion",
						data: {
							completion: {
								onboarding_completed: true,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to complete onboarding");
			}

			setCurrentStep(15);
		} catch (error) {
			console.error("Error completing onboarding:", error);
			alert("Error completing your onboarding. Please try again.");
		}
	};

	const renderStep = () => {
		const stepProps: StepProps = {
			onNext: handleNext,
			onBack: handleBack,
			initialData: formData,
		};

		switch (currentStep) {
			case 1:
				return <LocationStep {...stepProps} />;
			case 2:
				return <BirthsAttendedStep {...stepProps} />;
			case 3:
				return <ProfessionalInfoStep {...stepProps} />;
			case 4:
				return <ExperienceStep {...stepProps} />;
			case 5:
				return <ProfilePreferencesStep {...stepProps} />;
			case 6:
				return <ExcludedProvidersStep {...stepProps} />;
			case 7:
				return <PackagesStep {...stepProps} />;
			case 8:
				return <PhotoUploadStep {...stepProps} />;
			case 9:
				return <CertificationsStep {...stepProps} />;
			case 10:
				return <VideoStep {...stepProps} />;
			case 11:
				return <CalendarStep {...stepProps} />;
			case 12:
				return <MonthsAvailableStep {...stepProps} />;
			case 13:
				return <AvailabilitySettingsStep {...stepProps} />;
			case 14:
				return <StripeStep {...stepProps} />;
			case 15:
				return <CompletionStep onBack={handleBack} />;
			default:
				return <LocationStep {...stepProps} />;
		}
	};

	if (authLoading || loading) {
		return (
			<div className="min-h-screen bg-gray-50 py-8 px-4">
				<div className="max-w-4xl mx-auto">
					<div className="flex items-center justify-center h-64">
						<div className="text-center">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
							<p className="text-gray-600">Loading onboarding...</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (!user) {
		return (
			<div className="min-h-screen bg-gray-50 py-8 px-4">
				<div className="max-w-4xl mx-auto">
					<div className="flex items-center justify-center h-64">
						<div className="text-center">
							<p className="text-gray-600">
								Please sign in to continue with onboarding.
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 py-8 px-4">
			<div className="max-w-4xl mx-auto">
				{/* Header Section */}
				<motion.div
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="my-8 text-center"
				>
					<h1 className="text-4xl lg:text-6xl font-light text-black font-karla mb-8 leading-tight lg:leading-10">
						Please Complete Onboarding
					</h1>
					<p className="text-xl lg:text-[24px] text-black font-karla">
						In order to be listed in our network and connect with families, you
						must complete the onboarding process.
					</p>
				</motion.div>

				{renderStep()}
			</div>
		</div>
	);
};
